import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, where, addDoc, doc, updateDoc } from "firebase/firestore";
import axios from "axios";
import logo from "../assets/logo.jpg";
import Messages from "./Messages";
import ClientFileManager from "./ClientFileManager";
import { sendNotification } from "./notify";
import GlobalSearch from "./GlobalSearch";
import AdvancedSearch from "./AdvancedSearch";

const CLOUD_NAME = "dscxfsvrf";
const UPLOAD_PRESET = "ficsori-portal";
const RAW_UPLOAD_PRESET = "ficsori-portal-raw";

const CLIENT_UPLOAD_FOLDERS = [
  { id:"bank",     name:"Bank Statements",      icon:"🏦" },
  { id:"loans",    name:"Loan Documents",        icon:"🏛" },
  { id:"checks",   name:"Checks & Receipts",     icon:"🧾" },
  { id:"tax",      name:"Tax Documents",         icon:"📋" },
  { id:"forms1099",name:"1099 / Tax Forms",      icon:"📑" },
  { id:"payroll",  name:"Payroll Documents",     icon:"💼" },
  { id:"other",    name:"Other / Miscellaneous", icon:"📎" },
  { id:"custom",   name:"+ Add Custom Category", icon:"✏️" },
];

const ALL_FOLDERS = [
  { id:"bank",        name:"Bank Statements",         icon:"🏦", color:"#185FA5", bg:"#E6F1FB" },
  { id:"tax",         name:"Tax Returns",              icon:"📋", color:"#085041", bg:"#E1F5EE" },
  { id:"bookkeeping", name:"QBO / Bookkeeping",        icon:"📊", color:"#534AB7", bg:"#EEEDFE" },
  { id:"payroll",     name:"Payroll",                  icon:"💼", color:"#854F0B", bg:"#FAEEDA" },
  { id:"loans",       name:"Loan Statements",          icon:"🏛", color:"#3B6D11", bg:"#EAF3DE" },
  { id:"cfo",         name:"CFO Reports & Scorecards", icon:"📈", color:"#993C1D", bg:"#FAECE7" },
  { id:"engagement",  name:"Engagement Letters",       icon:"📄", color:"#C0111F", bg:"#fdf0f0" },
  { id:"corporate",   name:"Corporate Docs",           icon:"🏢", color:"#1a1a18", bg:"#f5f5f0" },
  { id:"checks",      name:"Checks & Receipt Images",  icon:"🧾", color:"#0C447C", bg:"#E6F1FB" },
];

const STATUS_COLORS = {
  Paid:["#EAF3DE","#3B6D11"], Sent:["#E6F1FB","#185FA5"],
  Overdue:["#FCEBEB","#A32D2D"], Draft:["#f5f5f0","#888"],
};
const TASK_STATUS_COLORS = {
  "To Do":["#f5f5f0","#555"], "In Progress":["#E6F1FB","#185FA5"],
  "Needs Review":["#FAEEDA","#854F0B"], "Completed":["#EAF3DE","#3B6D11"],
};

const S = {
  card:{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px",marginBottom:16},
  btn:{fontSize:12,padding:"6px 14px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"6px 14px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnSm:{fontSize:11,padding:"3px 9px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"},
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 8px",borderRadius:20,background:bg,color:c,fontWeight:500}),
  input:{width:"100%",padding:"6px 9px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
};

function formatSize(bytes){
  if(bytes>1048576) return (bytes/1048576).toFixed(1)+" MB";
  return Math.round(bytes/1024)+" KB";
}

function fileIcon(name){
  const ext=(name||"").split(".").pop().toLowerCase();
  if(ext==="pdf") return "📄";
  if(["xlsx","xls","csv"].includes(ext)) return "📊";
  if(["doc","docx"].includes(ext)) return "📝";
  if(["jpg","jpeg","png","gif"].includes(ext)) return "🖼";
  return "📎";
}

function AlertBanner({ files, tasks, invoices, letters }) {
  const alerts = [];

  // Pending signatures on engagement letters
  const pendingLetters = letters.filter(l=>!l.clientSigned);
  if(pendingLetters.length>0)
    alerts.push({type:"signature", icon:"✍️", color:"#C0111F", bg:"#fdf0f0", border:"#FCEBEB",
      text:`${pendingLetters.length} engagement letter${pendingLetters.length>1?"s":""} awaiting your signature`});

  // Overdue invoices
  const overdueInvoices = invoices.filter(i=>i.status==="Overdue");
  if(overdueInvoices.length>0)
    alerts.push({type:"invoice", icon:"⚠️", color:"#A32D2D", bg:"#FCEBEB", border:"#FCEBEB",
      text:`${overdueInvoices.length} overdue invoice${overdueInvoices.length>1?"s":""} — please review`});

  // Tasks needing review
  const reviewTasks = tasks.filter(t=>t.status==="Needs Review");
  if(reviewTasks.length>0)
    alerts.push({type:"task", icon:"👀", color:"#854F0B", bg:"#FAEEDA", border:"#EF9F27",
      text:`${reviewTasks.length} item${reviewTasks.length>1?"s":""} need${reviewTasks.length===1?"s":""} your review`});

  if(alerts.length===0) return null;

  return (
    <div style={{marginBottom:16}}>
      {alerts.map((a,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:a.bg,border:"0.5px solid "+a.border,borderRadius:10,marginBottom:8}}>
          <span style={{fontSize:18}}>{a.icon}</span>
          <span style={{fontSize:13,color:a.color,fontWeight:500}}>{a.text}</span>
        </div>
      ))}
    </div>
  );
}

function TaskCard({ task: t, clientName, userEmail, onFolderNav }) {
  const [responding, setResponding] = useState(false);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submitResponse = async () => {
    if(!comment.trim()) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "tasks", t.firestoreId), {
        status: "Completed",
        clientResponse: comment.trim(),
        clientRespondedAt: new Date().toISOString(),
        clientRespondedBy: userEmail,
        hasClientResponse: true,
      });
      sendNotification("client_responded",{
        recipientEmail: "frank@ficsoriaccounting.com",
        recipientName: "Frank Ficsori",
        clientName: clientName,
        details: comment.trim(),
      });
      setResponding(false);
      setComment("");
    } catch(e) { alert("Failed to save response: "+e.message); }
    setSaving(false);
  };

  const borderColor = t.status==="Completed" ? "#3B6D11" : t.status==="Needs Review" ? "#854F0B" : "#C0111F";

  return (
    <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderLeft:"3px solid "+borderColor,borderRadius:12,padding:"16px 20px",marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
        <div style={{fontSize:13,fontWeight:500,flex:1,marginRight:12}}>{t.title}</div>
        <span style={S.badge(...(TASK_STATUS_COLORS[t.status||"To Do"]||["#f5f5f0","#555"]))}>{t.status||"To Do"}</span>
      </div>
      {t.notes&&<div style={{fontSize:12,color:"#555",marginBottom:10,lineHeight:1.7,background:"#fafaf8",padding:"8px 12px",borderRadius:8}}>{t.notes}</div>}
      <div style={{display:"flex",gap:16,fontSize:11,color:"#888",marginBottom:10}}>
        {t.due&&<span>📅 Due: {new Date(t.due+"T12:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</span>}
        {t.folderRef&&<span style={{color:"#185FA5",cursor:"pointer",textDecoration:"underline",fontWeight:500}} onClick={()=>onFolderNav&&onFolderNav(t.folderRef)}>📁 {t.folderRef} →</span>}
        {t.category&&<span>🏷 {t.category}</span>}
      </div>

      {/* Client response if already submitted */}
      {t.clientResponse && (
        <div style={{background:"#EAF3DE",border:"0.5px solid #B5D8A4",borderRadius:8,padding:"10px 12px",marginBottom:10}}>
          <div style={{fontSize:11,color:"#3B6D11",fontWeight:500,marginBottom:4}}>✓ Your response — {new Date(t.clientRespondedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>
          <div style={{fontSize:12,color:"#1a1a18"}}>{t.clientResponse}</div>
        </div>
      )}

      {/* Response area */}
      {t.status!=="Completed" && !responding && (
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <button style={{...S.btnPrimary,fontSize:11}} onClick={()=>setResponding(true)}>
            ✍️ Respond &amp; Mark Complete
          </button>
          <button style={{...S.btn,fontSize:11}} onClick={()=>setResponding(true)}>
            💬 Leave a note
          </button>
        </div>
      )}

      {responding && (
        <div style={{marginTop:10,padding:"12px 14px",background:"#f5f5f0",borderRadius:8}}>
          <div style={{fontSize:11,color:"#888",marginBottom:6}}>Your response or note to Ficsori Accounting:</div>
          <textarea
            style={{...S.input,resize:"vertical",minHeight:80,marginBottom:8}}
            value={comment}
            onChange={e=>setComment(e.target.value)}
            placeholder="e.g. I uploaded the Bank of America and Chase statements. The Amex and Capital One statements won't be available until the 18th — I'll upload them then."
            autoFocus
          />
          <div style={{display:"flex",gap:8}}>
            <button style={{...S.btnPrimary,fontSize:11}} onClick={submitResponse} disabled={saving||!comment.trim()}>
              {saving?"Saving...":"Submit & Mark Complete"}
            </button>
            <button style={{...S.btn,fontSize:11}} onClick={()=>{setResponding(false);setComment("");}}>Cancel</button>
          </div>
        </div>
      )}

      {t.status==="Completed" && !t.clientResponse && (
        <div style={{fontSize:11,color:"#3B6D11",marginTop:4}}>✓ Marked complete</div>
      )}
    </div>
  );
}

function ClientFAQItem({icon, q, a}){
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{border:"0.5px solid #e0dfd8",borderRadius:10,marginBottom:8,overflow:"hidden",background:"#fff"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",cursor:"pointer"}}>
        <span style={{fontSize:18,flexShrink:0}}>{icon}</span>
        <div style={{fontSize:13,fontWeight:500,flex:1}}>{q}</div>
        <span style={{fontSize:12,color:"#aaa"}}>{open?"▲":"▼"}</span>
      </div>
      {open&&<div style={{padding:"0 16px 14px 48px",fontSize:13,color:"#555",lineHeight:1.75,borderTop:"0.5px solid #f0efe8"}}><div style={{height:8}}/>{a}</div>}
    </div>
  );
}

export default function ClientPortal({ user, onLogout, clientName }) {
  const [page, setPage] = useState(()=>localStorage.getItem("ficsori_client_page")||"home");

  const navigate = (p) => {
    setPage(p);
    localStorage.setItem("ficsori_client_page", p);
  };
  const [files, setFiles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [letters, setLetters] = useState([]);
  const [folderFilter, setFolderFilter] = useState(null);
  const [advSearchQuery, setAdvSearchQuery] = useState("");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [openTasksCount, setOpenTasksCount] = useState(0);

  useEffect(()=>{
    if(!clientName) return;
    const unsub = onSnapshot(
      query(collection(db,"tasks"), where("client","==",clientName)),
      snap=>setOpenTasksCount(snap.docs.filter(d=>d.data().status!=="Completed").length)
    );
    return ()=>unsub();
  },[clientName]);

  useEffect(()=>{
    if(!clientName) return;
    const threadId = clientName.replace(/\s+/g,"_").replace(/[^a-zA-Z0-9_]/g,"");
    const unsub = onSnapshot(collection(db,"messages",threadId,"thread"), snap=>{
      const unread = snap.docs.filter(d=>{ const m=d.data(); return m.senderIsAdmin && !m.readAt; }).length;
      setUnreadMessages(unread);
    });
    return ()=>unsub();
  },[clientName]);
  const [uploading, setUploading] = useState(false);
  const [customFolder, setCustomFolder] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFolder, setUploadFolder] = useState(CLIENT_UPLOAD_FOLDERS[0].id);
  const [uploadYear, setUploadYear] = useState("2026");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  // Load client's data from Firestore
  useEffect(()=>{
    if(!clientName) return;

    const unsubFiles = onSnapshot(
      query(collection(db,"files"), where("client","==",clientName)),
      snap=>setFiles(snap.docs.map(d=>({...d.data(),firestoreId:d.id})))
    );
    const unsubTasks = onSnapshot(
      query(collection(db,"tasks"), where("client","==",clientName)),
      snap=>setTasks(snap.docs.map(d=>({...d.data(),firestoreId:d.id})))
    );
    const unsubInvoices = onSnapshot(
      query(collection(db,"invoices"), where("client","==",clientName)),
      snap=>setInvoices(snap.docs.map(d=>({...d.data(),firestoreId:d.id})))
    );
    const unsubLetters = onSnapshot(
      query(collection(db,"engagementLetters"), where("client","==",clientName)),
      snap=>setLetters(snap.docs.map(d=>({...d.data(),firestoreId:d.id})))
    );

    return ()=>{ unsubFiles(); unsubTasks(); unsubInvoices(); unsubLetters(); };
  },[clientName]);

  const uploadFile = async (file) => {
    const folderObj = uploadFolder==="custom" ? {name:customFolder||"Other",id:"custom"} : CLIENT_UPLOAD_FOLDERS.find(f=>f.id===uploadFolder);
    setUploading(true); setUploadProgress(0);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    formData.append("folder", `ficsori-portal/${(clientName||"").replace(/\s/g,"_")}/${uploadFolder}/${uploadYear}`);
    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`, formData,
        { onUploadProgress: e=>setUploadProgress(Math.round(e.loaded*100/e.total)) }
      );
      await addDoc(collection(db,"files"),{
        name: file.name, client: clientName,
        folder: uploadFolder, folderName: uploadFolder==="custom" ? (customFolder||"Other") : (folderObj?.name||uploadFolder),
        year: uploadYear, size: formatSize(file.size),
        url: res.data.secure_url, cloudinaryId: res.data.public_id,
        uploadedBy: user.email, uploadedAt: new Date().toISOString(),
        version: 1, uploadedByClient: true,
        versions:[{version:1,url:res.data.secure_url,uploadedAt:new Date().toISOString(),uploadedBy:user.email}],
      });
      sendNotification("client_uploaded",{
        recipientEmail: "frank@ficsoriaccounting.com",
        recipientName: "Frank Ficsori",
        clientName: clientName,
        details: `${clientName} uploaded: ${file.name}`,
      });
      alert(`"${file.name}" uploaded successfully!`);
    } catch(e){ alert("Upload failed. Please try again."); }
    setUploading(false); setUploadProgress(0);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); if(e.dataTransfer.files[0]) uploadFile(e.dataTransfer.files[0]); };
  const handleFileChange = (e) => { if(e.target.files[0]) uploadFile(e.target.files[0]); e.target.value=""; };

  const openTasks = tasks.filter(t=>t.status!=="Completed");
  const pendingInvoices = invoices.filter(i=>i.status!=="Paid");
  const pendingLetters = letters.filter(l=>!l.clientSigned);

  const today = new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"});

  const NAV = [
    {id:"home", label:"Home", icon:"🏠"},
    {id:"files", label:"My Documents", icon:"📁"},
    {id:"upload", label:"Upload Files", icon:"⬆"},
    {id:"tasks", label:"Action Items", icon:"✅", badge: openTasks.length},
    {id:"invoices", label:"Invoices", icon:"💳", badge: pendingInvoices.length},
    {id:"letters", label:"Agreements", icon:"📝", badge: pendingLetters.length},
    {id:"messages", label:"Messages", icon:"💬", badge: true},
    {id:"help", label:"Help & FAQ", icon:"❓"},
  ];

  return (
    <div style={{display:"flex",height:"100vh",fontFamily:"sans-serif",background:"#f5f5f0",color:"#1a1a18"}}>
      {/* Sidebar */}
      <div style={{width:220,minWidth:220,background:"#fff",borderRight:"0.5px solid #e0dfd8",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"14px 16px 10px",borderBottom:"0.5px solid #e0dfd8",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
          <img src={logo} alt="Ficsori Accounting" style={{height:110,objectFit:"contain",maxWidth:260}}/>
          <div style={{fontSize:10,color:"#aaa"}}>Client Portal</div>
        </div>

        {/* Client name */}
        <div style={{padding:"12px 16px",borderBottom:"0.5px solid #f0efe8",background:"#fafaf8"}}>
          <div style={{fontSize:11,color:"#888"}}>Welcome back,</div>
          <div style={{fontSize:13,fontWeight:500,color:"#1a1a18",marginTop:1}}>{clientName}</div>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
          {NAV.map(n=>(
            <div key={n.id} onClick={()=>navigate(n.id)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 16px",fontSize:13,color:page===n.id?"#C0111F":"#555",cursor:"pointer",borderLeft:page===n.id?"2px solid #C0111F":"2px solid transparent",background:page===n.id?"#fdf0f0":"transparent",fontWeight:page===n.id?500:400}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:15}}>{n.icon}</span>
                {n.label}
              </div>
              {n.id==="messages"&&unreadMessages>0&&<span style={{background:"#C0111F",color:"#fff",fontSize:10,fontWeight:500,padding:"1px 6px",borderRadius:10,minWidth:16,textAlign:"center"}}>{unreadMessages}</span>}
              {n.id==="tasks"&&openTasksCount>0&&<span style={{background:"#C0111F",color:"#fff",fontSize:10,fontWeight:500,padding:"1px 6px",borderRadius:10,minWidth:16,textAlign:"center"}}>{openTasksCount}</span>}
            </div>
          ))}
        </div>

        <div style={{padding:"10px 16px 14px",borderTop:"0.5px solid #e0dfd8"}}>
          <div style={{fontSize:10,color:"#aaa",marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email}</div>
          <button style={{...S.btn,width:"100%",fontSize:11,color:"#A32D2D"}} onClick={onLogout}>Sign Out</button>
        </div>
      </div>

      {/* Main content */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Top bar */}
        <div style={{background:"#fff",borderBottom:"0.5px solid #e0dfd8",padding:"0 24px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:500}}>
            {NAV.find(n=>n.id===page)?.icon} {NAV.find(n=>n.id===page)?.label||"Home"}
          </div>
          <GlobalSearch
            isAdmin={false}
            clientName={clientName}
            onNavigate={(dest, query)=>{
              if(dest==="advanced-search"){ setAdvSearchQuery(query||""); navigate("advanced-search"); }
              else navigate(dest);
            }}
          />
          <div style={{fontSize:12,color:"#888"}}>{today}</div>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"20px 24px"}}>

          {/* HOME */}
          {page==="home" && (
            <div>
              <AlertBanner files={files} tasks={openTasks} invoices={invoices} letters={letters}/>

              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
                {[
                  ["My Documents", files.length, "#185FA5", "files"],
                  ["Action Items", openTasks.length, openTasks.length>0?"#C0111F":"#3B6D11", "tasks"],
                  ["Pending Invoices", pendingInvoices.length, pendingInvoices.length>0?"#854F0B":"#3B6D11", "invoices"],
                  ["Awaiting Signature", pendingLetters.length, pendingLetters.length>0?"#C0111F":"#3B6D11", "letters"],
                ].map(([l,v,c,nav])=>(
                  <div key={l} style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:10,padding:"14px 16px",cursor:"pointer"}} onClick={()=>setPage(nav)}>
                    <div style={{fontSize:12,color:"#888",marginBottom:6}}>{l}</div>
                    <div style={{fontSize:24,fontWeight:500,color:c}}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Recent files */}
              <div style={S.card}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontSize:13,fontWeight:500}}>Recent Documents</div>
                  <span style={{fontSize:11,color:"#C0111F",cursor:"pointer"}} onClick={()=>navigate("files")}>View all →</span>
                </div>
                {files.length===0
                  ?<div style={{fontSize:13,color:"#aaa",textAlign:"center",padding:"20px 0"}}>No documents yet.</div>
                  :files.slice(0,4).map(f=>(
                    <div key={f.firestoreId} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"0.5px solid #f0efe8"}}>
                      <span style={{fontSize:18,flexShrink:0}}>{fileIcon(f.name)}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                        <div style={{fontSize:11,color:"#888"}}>{f.folderName||f.folder} · {f.year} · {f.size}</div>
                      </div>
                      <a href={f.url} target="_blank" rel="noreferrer" style={{...S.btnSm,textDecoration:"none",color:"#1a1a18",fontSize:11}}>↓</a>
                    </div>
                  ))
                }
              </div>

              {/* Open action items */}
              {openTasks.length>0&&(
                <div style={S.card}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <div style={{fontSize:13,fontWeight:500}}>Action Items</div>
                    <span style={{fontSize:11,color:"#C0111F",cursor:"pointer"}} onClick={()=>navigate("tasks")}>View all →</span>
                  </div>
                  {openTasks.slice(0,3).map(t=>(
                    <div key={t.firestoreId} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"0.5px solid #f0efe8"}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:500}}>{t.title}</div>
                        {t.notes&&<div style={{fontSize:11,color:"#888",marginTop:2}}>{t.notes.slice(0,80)}{t.notes.length>80?"...":""}</div>}
                      </div>
                      <span style={S.badge(...(TASK_STATUS_COLORS[t.status||"To Do"]||["#f5f5f0","#555"]))}>{t.status||"To Do"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FILES */}
          {page==="files" && (
            <ClientFileManager
              clientName={clientName}
              folderFilter={folderFilter}
              onClearFilter={()=>setFolderFilter(null)}
              userEmail={user.email}
            />
          )}
          {/* UPLOAD */}
          {page==="upload" && (
            <div style={S.card}>
              <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Upload Documents</div>
              <div style={{fontSize:12,color:"#888",marginBottom:16}}>Send documents securely to Ficsori Accounting. Your files are encrypted and only accessible by your accounting team.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                <div>
                  <div style={{fontSize:11,color:"#888",marginBottom:3}}>Folder</div>
                  <select style={S.input} value={uploadFolder} onChange={e=>setUploadFolder(e.target.value)}>
                    {CLIENT_UPLOAD_FOLDERS.map(f=><option key={f.id} value={f.id}>{f.icon} {f.name}</option>)}
                  </select>
                  {uploadFolder==="custom"&&(
                    <div style={{marginTop:6}}>
                      <input style={S.input} value={customFolder} onChange={e=>setCustomFolder(e.target.value)} placeholder="e.g. Insurance Documents, HOA Records..."/>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{fontSize:11,color:"#888",marginBottom:3}}>Year</div>
                  <select style={S.input} value={uploadYear} onChange={e=>setUploadYear(e.target.value)}>
                    {["2026","2025","2024","2023","2022"].map(y=><option key={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div
                onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                onDragLeave={()=>setDragOver(false)}
                onDrop={handleDrop}
                onClick={()=>!uploading&&fileRef.current.click()}
                style={{border:"1.5px dashed "+(dragOver?"#C0111F":"#ccc"),borderRadius:12,padding:32,textAlign:"center",cursor:uploading?"wait":"pointer",background:dragOver?"#fdf0f0":"transparent"}}
              >
                <div style={{fontSize:28,marginBottom:10}}>⬆</div>
                <div style={{fontSize:13,fontWeight:500}}>{uploading?`Uploading... ${uploadProgress}%`:"Drop your file here or click to browse"}</div>
                <div style={{fontSize:12,color:"#888",marginTop:4}}>PDF, XLSX, DOCX, images — sent directly to Ficsori Accounting</div>
                {uploading&&<div style={{marginTop:12,height:4,background:"#e0dfd8",borderRadius:2}}><div style={{height:4,background:"#C0111F",borderRadius:2,width:uploadProgress+"%",transition:"width 0.3s"}}/></div>}
                {!uploading&&<button style={{...S.btnPrimary,marginTop:12}} onClick={e=>{e.stopPropagation();fileRef.current.click();}}>Choose File</button>}
              </div>
              <input ref={fileRef} type="file" style={{display:"none"}} onChange={handleFileChange} accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg"/>
              <div style={{marginTop:14,padding:"12px 14px",background:"#f5f5f0",borderRadius:8,fontSize:12,color:"#888"}}>
                <strong style={{color:"#1a1a18"}}>You can upload to:</strong> Bank Statements, Loan Statements, and Checks & Receipt Images. For other documents, please contact your accountant.
              </div>
            </div>
          )}

          {/* TASKS / ACTION ITEMS */}
          {page==="tasks" && (
            <div>
              <div style={{fontSize:12,color:"#888",marginBottom:14}}>Items your accounting team has flagged for your attention. You can respond with a note and mark items complete.</div>
              {tasks.length===0
                ?<div style={{...S.card,textAlign:"center",padding:"40px 0",color:"#aaa"}}>No action items at this time.</div>
                :tasks.map(t=><TaskCard key={t.firestoreId} task={t} clientName={clientName} userEmail={user.email} onFolderNav={(folderName)=>{ setFolderFilter(folderName); navigate("files"); }}/>)
              }
            </div>
          )}

          {/* INVOICES */}
          {page==="invoices" && (
            <div>
              <div style={{fontSize:12,color:"#888",marginBottom:14}}>Invoices from Ficsori Accounting for your services.</div>
              {invoices.length===0
                ?<div style={{...S.card,textAlign:"center",padding:"40px 0",color:"#aaa"}}>No invoices yet.</div>
                :<div style={S.card}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr>
                      <th style={{textAlign:"left",padding:"6px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12}}>#</th>
                      <th style={{textAlign:"left",padding:"6px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12}}>Service</th>
                      <th style={{textAlign:"left",padding:"6px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12}}>Amount</th>
                      <th style={{textAlign:"left",padding:"6px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12}}>Due</th>
                      <th style={{textAlign:"left",padding:"6px 10px",borderBottom:"0.5px solid #e0dfd8",color:"#888",fontWeight:500,fontSize:12}}>Status</th>
                    </tr></thead>
                    <tbody>
                      {invoices.map(inv=>(
                        <tr key={inv.firestoreId}>
                          <td style={{padding:"8px 10px",borderBottom:"0.5px solid #f0efe8",fontSize:12,color:"#888"}}>{inv.num}</td>
                          <td style={{padding:"8px 10px",borderBottom:"0.5px solid #f0efe8",fontSize:12}}>{inv.service}</td>
                          <td style={{padding:"8px 10px",borderBottom:"0.5px solid #f0efe8",fontSize:12,fontWeight:500}}>{inv.amount}</td>
                          <td style={{padding:"8px 10px",borderBottom:"0.5px solid #f0efe8",fontSize:12,color:"#888"}}>{inv.due||"—"}</td>
                          <td style={{padding:"8px 10px",borderBottom:"0.5px solid #f0efe8",fontSize:12}}>
                            <span style={S.badge(...(STATUS_COLORS[inv.status]||["#f5f5f0","#888"]))}>{inv.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              }
            </div>
          )}

          {/* ENGAGEMENT LETTERS */}
          {page==="letters" && (
            <div>
              <div style={{fontSize:12,color:"#888",marginBottom:14}}>Engagement agreements and letters from Ficsori Accounting.</div>
              {letters.length===0
                ?<div style={{...S.card,textAlign:"center",padding:"40px 0",color:"#aaa"}}>No engagement letters yet.</div>
                :letters.map(l=>(
                  <div key={l.firestoreId} style={{...S.card,borderLeft:"3px solid "+(l.clientSigned?"#3B6D11":"#C0111F")}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500}}>{l.template||"Engagement Letter"}</div>
                        <div style={{fontSize:11,color:"#888",marginTop:2}}>Sent {l.sentAt?new Date(l.sentAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}):"—"}</div>
                      </div>
                      <span style={S.badge(l.clientSigned?"#EAF3DE":"#fdf0f0",l.clientSigned?"#3B6D11":"#C0111F")}>
                        {l.clientSigned?"Signed ✓":"Awaiting your signature"}
                      </span>
                    </div>
                    {!l.clientSigned&&(
                      <div style={{marginTop:10,padding:"10px 14px",background:"#fdf0f0",borderRadius:8,fontSize:12,color:"#C0111F"}}>
                        ✍️ This agreement requires your signature. Please contact Ficsori Accounting to sign electronically.
                      </div>
                    )}
                  </div>
                ))
              }
            </div>
          )}


          {/* ADVANCED SEARCH */}
          {page==="advanced-search" && (
            <AdvancedSearch initialQuery={advSearchQuery||""} isAdmin={false} clientName={clientName}/>
          )}

          {/* MESSAGES */}
          {page==="messages" && (
            <Messages isAdmin={false} currentUserName={clientName} assignedClients={[]}/>
          )}

          {/* HELP */}
          {page==="help" && (
            <div>
              <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Help & FAQ</div>
              <div style={{fontSize:12,color:"#888",marginBottom:16}}>Everything you need to know about your Ficsori Accounting client portal.</div>
              {[
                {icon:"🏠",q:"What is this portal?",a:"This is your secure, private client portal provided by Ficsori Accounting. It is the central place to share documents, view invoices, sign engagement letters, send messages, and communicate directly with your accounting team. Everything is encrypted and only accessible by you and Ficsori Accounting."},
                {icon:"📁",q:"How do I view and manage my documents?",a:"Click My Documents in the left menu. You'll see a full file manager with folders and subfolders. You can view files (👁), download them (↓), rename (✏), move between folders (⇄), and delete files. You can also create your own folders and subfolders to stay organized."},
                {icon:"⬆",q:"How do I upload documents to my accountant?",a:"Click My Documents and use the ⬆ Upload Files/Folders button. You can browse for files, browse for an entire folder, or drag and drop files or folders directly. If you drop a folder, it is automatically created as a subfolder and all files inside are uploaded at once. Your uploads appear instantly in the Ficsori Accounting portal."},
                {icon:"✅",q:"What are Action Items?",a:"Action Items are requests or tasks from Ficsori Accounting asking you to provide specific documents or information. A red number badge on the Action Items menu shows how many open requests you have. Each item shows what's needed. You can leave a note and mark it complete when done. Your accountant receives an email notification when you respond."},
                {icon:"💬",q:"How do I send a message to my accountant?",a:"Click Messages in the left menu. Type your message and press Enter (or Shift+Enter for a new line). You can attach files by clicking 📎, add a subject line by clicking ≡, and edit or delete sent messages by hovering over them. You'll see ✓ when sent and ✓✓ in green when your accountant has read the message."},
                {icon:"💳",q:"How do I view my invoices?",a:"Click Invoices in the left menu to see all invoices from Ficsori Accounting. Each invoice shows the service, amount, due date, and status (Draft, Sent, Paid, or Overdue). For questions about an invoice, send a message through the portal or contact Frank at frank@ficsoriaccounting.com."},
                {icon:"📝",q:"What are Agreements?",a:"Agreements are engagement letters from Ficsori Accounting outlining the services being provided and the terms of your engagement. Full e-signature is coming soon. For now, contact Ficsori Accounting to discuss any agreement questions."},
                {icon:"🔔",q:"Will I get email notifications?",a:"Yes. You receive email notifications when: Ficsori Accounting sends you a document request, and when you receive a new message. All emails come from frank@ficsoriaccounting.com. If you don't see them in your inbox, check your spam folder and mark them as Not Spam. Add frank@ficsoriaccounting.com to your contacts to ensure reliable delivery."},
                {icon:"👥",q:"Can two people in my company share the same portal?",a:"Yes. If two people (e.g. husband and wife, or two business partners) need portal access, ask Frank to set up a secondary email. Both will receive separate invitations and can log in independently. You both see the same files, action items, invoices, and messages."},
                {icon:"🛡",q:"What security standards protect my documents?",a:"Your documents are stored on Google Firebase and Cloudinary — enterprise-grade platforms trusted by financial institutions and Fortune 500 companies worldwide. Google Firebase maintains SOC 2, ISO 27001, PCI DSS, and HIPAA compliance certifications. Cloudinary holds SOC 2 Type II and ISO 27001 certifications. All data is encrypted using AES-256 at rest and TLS 1.3 in transit — the same standards used by major banks."},
                {icon:"🔒",q:"Is my data secure?",a:"Yes. All data is encrypted at rest and in transit. Your portal is protected by your personal email and password — other clients cannot see your information. Firebase Authentication blocks unauthorized access at the database level, not just the interface. Only you and Ficsori Accounting can access your data."},
                {icon:"🏛",q:"My company has compliance requirements — can I share security documentation?",a:"Yes. The following resources can be shared with your compliance or back office team: Google Cloud Compliance Certifications: cloud.google.com/security/compliance · Cloudinary Trust Center: cloudinary.com/trust · For a specific security summary or additional documentation, contact Frank Ficsori at frank@ficsoriaccounting.com or (941) 780-4662."},
                {icon:"🔑",q:"I forgot my password — what do I do?",a:"Click Forgot your password? on the login page at portal.ficsoriaccounting.com. Enter your email and you'll receive a password reset link. Click the link, set a new password, and log in normally."},
                {icon:"📱",q:"Can I use the portal on my phone?",a:"Yes. The portal works on any modern browser — Chrome, Safari, Firefox — on phone, tablet, or computer. You can upload photos of receipts or documents directly from your phone camera through the upload feature."},
                {icon:"❓",q:"I have a question not answered here — what do I do?",a:"Contact Ficsori Accounting directly: Email: frank@ficsoriaccounting.com · Phone: (941) 780-4662 · Website: www.ficsoriaccounting.com"},
              ].map((item,i)=>(
                <ClientFAQItem key={i} icon={item.icon} q={item.q} a={item.a}/>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
